import { Controller } from '@hotwired/stimulus'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

export default class extends Controller {
  static targets = [
    'statusDisplay',
    'progressDisplay',
    'audioFilesList',
    'feedbackContent',
    'chatMessages',
    'chatForm',
    'chatInput',
    'chatSubmit',
    'chatError',
  ]

  static values = {
    sessionId: Number,
    statusUrl: String,
    sendMessageUrl: String,
    isProcessing: Boolean,
    chatAvailable: Boolean,
  }

  MAX_POLL_RETRIES = 10

  connect () {
    // markedの設定
    marked.setOptions({
      breaks: true,
      gfm: true,
    })

    // AIフィードバックをマークダウンでレンダリング
    if (this.hasFeedbackContentTarget) {
      const rawText = this.feedbackContentTarget.textContent
      this.feedbackContentTarget.innerHTML = this.renderMarkdown(rawText)
    }

    // 既存のAIメッセージをマークダウンでレンダリング
    this.element.querySelectorAll('.chat-text[data-role="assistant"]').forEach((el) => {
      el.classList.add('markdown-body')
      el.innerHTML = this.renderMarkdown(el.textContent)
    })

    if (this.isProcessingValue) {
      this.pollRetryCount = 0
      setTimeout(() => this.updateStatus(), 3000)
    }

    if (this.chatAvailableValue && this.hasChatMessagesTarget) {
      this.scrollToBottom()
    }
  }

  // マークダウンをHTMLに変換（DOMPurifyでXSS対策）
  renderMarkdown (text) {
    return DOMPurify.sanitize(marked.parse(text))
  }

  // ステータスを更新
  updateStatus () {
    fetch(this.statusUrlValue)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        this.pollRetryCount = 0

        this.updateStatusDisplay(data)
        this.updateProgressDisplay(data)
        this.updateAudioFilesDisplay(data)

        // 完了時はリロード
        if (data.session.status === 'completed' || (data.feedback && data.feedback.generated)) {
          location.reload()
        } else if (data.transcript_ready && !data.feedback) {
          location.reload()
        } else if (['uploading', 'transcribing', 'merging', 'generating_feedback', 'splitting'].includes(data.session.status)) {
          setTimeout(() => this.updateStatus(), 5000)
        }
      })
      .catch(error => {
        console.error('Status update failed:', error)
        this.pollRetryCount = (this.pollRetryCount || 0) + 1
        if (this.pollRetryCount < this.MAX_POLL_RETRIES) {
          setTimeout(() => this.updateStatus(), 10000)
        } else {
          console.error('Status polling stopped after max retries')
        }
      })
  }

  updateStatusDisplay (data) {
    if (!this.hasStatusDisplayTarget) return

    let badgeClass = 'badge-secondary'
    let spinner = ''
    const status = data.session.status

    if (['uploading', 'transcribing', 'merging', 'generating_feedback', 'splitting'].includes(status)) {
      badgeClass = 'badge-warning'
      spinner = '<div class="spinner-border spinner-border-sm ml-2"></div>'
    } else if (['transcribed', 'completed', 'split_completed'].includes(status)) {
      badgeClass = 'badge-success'
    } else if (status === 'failed') {
      badgeClass = 'badge-danger'
    }

    let html = `<span class="badge badge-lg ${badgeClass}">${data.session.status_label}</span>${spinner}`
    if (status === 'failed' && data.session.error_message) {
      html += `<div class="alert alert-danger mt-2">${this.escapeHtml(data.session.error_message)}</div>`
    }
    this.statusDisplayTarget.innerHTML = html
  }

  updateProgressDisplay (data) {
    if (!this.hasProgressDisplayTarget) return

    const progressBar = this.progressDisplayTarget.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.style.width = data.session.progress_percentage + '%'
      progressBar.textContent = data.session.progress_percentage + '%'
    }

    const progressText = this.progressDisplayTarget.querySelector('p')
    if (progressText) {
      progressText.textContent = `ファイル: ${data.session.completed_files} / ${data.session.total_files} 完了`
    }
  }

  updateAudioFilesDisplay (data) {
    if (!this.hasAudioFilesListTarget) return

    data.audio_files.forEach(file => {
      const row = this.audioFilesListTarget.querySelector(`tr[data-file-id="${file.id}"]`)
      if (!row) return

      const statusCell = row.querySelector('.file-status')
      if (statusCell) {
        let badgeClass = 'badge-secondary'
        let spinner = ''
        if (file.status === 'transcribing' || file.status === 'splitting') {
          badgeClass = 'badge-warning'
          spinner = '<div class="spinner-border spinner-border-sm ml-1"></div>'
        } else if (file.status === 'transcribed' || file.status === 'split_completed') {
          badgeClass = 'badge-success'
        } else if (file.status === 'failed') {
          badgeClass = 'badge-danger'
        }
        statusCell.innerHTML = `<span class="badge ${badgeClass}">${file.status_label}</span>${spinner}`
      }

      const durationCell = row.querySelector('.file-duration')
      if (durationCell && file.duration_seconds) {
        const minutes = Math.floor(file.duration_seconds / 60)
        const seconds = file.duration_seconds % 60
        durationCell.textContent = `${minutes}分${seconds}秒`
      }

      const errorCell = row.querySelector('.file-error')
      if (errorCell) {
        errorCell.innerHTML = file.error_message
          ? `<span class="text-danger small">${this.escapeHtml(file.error_message)}</span>`
          : '-'
      }
    })
  }

  // チャット: メッセージ送信
  submitChat (event) {
    event.preventDefault()

    const message = this.chatInputTarget.value.trim()
    if (!message) return

    // ユーザーメッセージを表示
    this.addMessage('user', 'あなた', message)
    this.chatInputTarget.value = ''

    // ローディング表示
    this.setLoading(true)
    this.addLoadingMessage()

    // API呼び出し
    fetch(this.sendMessageUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify({ message }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        this.removeLoadingMessage()
        this.setLoading(false)

        if (data.success) {
          this.addMessage('assistant', 'AI', data.message.content, data.message.error_message)
        } else {
          this.showError(data.error || '応答の生成に失敗しました')
        }
      })
      .catch(error => {
        this.removeLoadingMessage()
        this.setLoading(false)
        console.error('Chat error:', error)
        this.showError('通信エラーが発生しました。再度お試しください。')
      })
  }

  // チャット: キーボードショートカット
  handleChatKeydown (event) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      this.submitChat(event)
    }
  }

  addMessage (role, roleLabel, content, errorMessage = null) {
    const badgeClass = role === 'user' ? 'badge-secondary' : 'badge-primary'
    const renderedContent = role === 'assistant' ? this.renderMarkdown(content) : this.escapeHtml(content)

    let html = `
      <div class="chat-message mb-3 chat-message-${role}">
        <div class="d-flex align-items-start">
          <div class="chat-role mr-3" style="min-width: 60px;">
            <span class="badge ${badgeClass}">${roleLabel}</span>
          </div>
          <div class="chat-content flex-grow-1">
            <div class="chat-text markdown-body">${renderedContent}</div>
    `
    if (errorMessage) {
      html += `
            <div class="text-danger small mt-1">
              <i class="fas fa-exclamation-circle mr-1"></i>${this.escapeHtml(errorMessage)}
            </div>
      `
    }
    html += `
          </div>
        </div>
      </div>
    `
    this.chatMessagesTarget.insertAdjacentHTML('beforeend', html)
    this.scrollToBottom()
  }

  addLoadingMessage () {
    const html = `
      <div class="chat-message mb-3 chat-message-loading" id="chat-loading">
        <div class="d-flex align-items-start">
          <div class="chat-role mr-3" style="min-width: 60px;">
            <span class="badge badge-primary">AI</span>
          </div>
          <div class="chat-content flex-grow-1">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="text-muted ml-2">応答を生成中...</span>
          </div>
        </div>
      </div>
    `
    this.chatMessagesTarget.insertAdjacentHTML('beforeend', html)
    this.scrollToBottom()
  }

  removeLoadingMessage () {
    const loading = document.getElementById('chat-loading')
    if (loading) loading.remove()
  }

  scrollToBottom () {
    if (this.hasChatMessagesTarget) {
      this.chatMessagesTarget.scrollTop = this.chatMessagesTarget.scrollHeight
    }
  }

  escapeHtml (text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  showError (message) {
    if (!this.hasChatErrorTarget) return

    this.chatErrorTarget.textContent = message
    this.chatErrorTarget.classList.remove('d-none')
    setTimeout(() => {
      this.chatErrorTarget.classList.add('d-none')
    }, 5000)
  }

  setLoading (loading) {
    if (this.hasChatSubmitTarget) {
      this.chatSubmitTarget.disabled = loading
      this.chatSubmitTarget.innerHTML = loading
        ? '<span class="spinner-border spinner-border-sm"></span>'
        : '<i class="fas fa-paper-plane mr-1"></i> 送信'
    }
    if (this.hasChatInputTarget) {
      this.chatInputTarget.disabled = loading
    }
  }
}
